/*
 * Copyright (c) 2022. Nedaware SL
 */

import {Injectable} from "@angular/core";
import {Filter} from "../../api/filter-interface";
import {
    BetweenRangeFilter,
    GreaterThanOrEqRangeFilter,
    GreaterThanRangeFilter,
    LowerThanOrEqRangeFilter,
    LowerThanRangeFilter
} from "../../basic-entity-back/filters/range-filters";
import {BooleanFilter} from "../../basic-entity-back/filters/boolean-filter";
import {GeneralSearchFilter} from "../../basic-entity-back/filters/general-search-filter";
import {ExistsFilter} from "../../basic-entity-back/filters/exists-filter";
import {NumericFilter} from "../../basic-entity-back/filters/numeric-filter";
import {
    AfterDateFilter,
    BeforeDateFilter,
    BetweenDateFilter,
    StrictlyAfterDateFilter,
    StrictlyBeforeDateFilter
} from "../../basic-entity-back/filters/date-filter";
import {IriSearchFilter} from "../../basic-entity-back/filters/iri-search-filter";
import {InternalPropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {Uri} from "../../api/uri";
import {Resource} from "../../api/resource";
import {InterfaceProviderService} from "../../basic-entity-back/services/interface-provider.service";
import {TypeStr} from "../../basic-entity-back/property-type/type-str";
import {FilterAndData} from "../../api/filter-list";
import {Params} from "@angular/router";
import {
    EndSearchFilter,
    ExactSearchFilter,
    MultipleSearchFilter,
    PartialSearchFilter,
    SimpleSearchFilter,
    StartSearchFilter,
    WordStartSearchFilter
} from "../../basic-entity-back/filters/search-filter";
import {EstadoPedidoClienteFilter} from "../../basic-entity-back/filters/estado-pedido-cliente-filter";
import {EstadoSolicitudClienteFilter} from "../../basic-entity-back/filters/estado-solicitud-cliente-filter";
import {isArray} from "rxjs/internal-compatibility";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

@Injectable({
    providedIn: "root"
})
export class RouteFilterTranslator {
    /** Prefix added to the filters keys when passing through the URL to avoid colliding with other params */
    private static readonly PARAMS_PREFIX = "$f-";
    /** URL identification of the filters */
    private static readonly FILTERS_NAMES: { [s: string]: Filter } = {
        gs: GeneralSearchFilter,
        b: BooleanFilter,
        ad: AfterDateFilter,
        bd: BeforeDateFilter,
        sad: StrictlyAfterDateFilter,
        sbd: StrictlyBeforeDateFilter,
        bwd: BetweenDateFilter,
        e: ExistsFilter,
        is: IriSearchFilter,
        n: NumericFilter,
        gt: GreaterThanRangeFilter,
        gte: GreaterThanOrEqRangeFilter,
        lt: LowerThanRangeFilter,
        lte: LowerThanOrEqRangeFilter,
        bt: BetweenRangeFilter,
        ms: MultipleSearchFilter,
        ss: SimpleSearchFilter,
        sss: StartSearchFilter,
        wss: WordStartSearchFilter,
        ps: PartialSearchFilter,
        es: EndSearchFilter,
        exs: ExactSearchFilter,
        // Estado del listado de pedidos del cliente (cada opción agrupa los estados reales que se ven igual).
        // Sin nombre aquí, el filtro no sobreviviría a ir y volver de la edición de un pedido
        exsep: EstadoPedidoClienteFilter,
        // Estado de los listados de diseños del cliente (el de la solicitud o, si tiene pedido, el del pedido)
        exses: EstadoSolicitudClienteFilter
    };

    constructor(private _interfaceProvider: InterfaceProviderService) {
    }


    /**
     * Prepares any value to be put in the url to filter by it
     */
    public static sParamValueFrom(propertyMap: InternalPropertyMap, value: any, shouldCast: boolean): string {
        // Una lista (casillas / selección múltiple del panel) se serializa elemento a elemento: un array de
        // Uri sobre una relación no puede pasar por el caster de NestedModel, que no sabe de Uri.
        if (value instanceof Array) {
            return JSON.stringify(value.map(subV => RouteFilterTranslator._sElementoAParam(propertyMap, subV, shouldCast)));
        }
        if (value instanceof Uri) {
            return JSON.stringify(value.toString());
        } else if (value instanceof Resource) {
            return JSON.stringify(value.iri.toString());
        }

        if (shouldCast && propertyMap && propertyMap.caster) {
            value = propertyMap.caster.fromModel(value);
        }

        return JSON.stringify(value);
    }

    /**
     * Un elemento de una lista de valores: un Uri o un Resource van como su IRI; el resto, como siempre
     * (con el caster de la propiedad si lo hay; sin caster, p. ej. el estado, una elección, va tal cual).
     */
    private static _sElementoAParam(propertyMap: InternalPropertyMap, valor: any, shouldCast: boolean): any {
        if (valor instanceof Uri) {
            return valor.toString();
        } else if (valor instanceof Resource) {
            return valor.iri.toString();
        }
        if (shouldCast && propertyMap && propertyMap.caster) {
            valor = propertyMap.caster.fromModel(valor);
        }
        // Se mantiene la segunda pasada que ya había para las listas (no cambia el contrato de las que
        // funcionan hoy); cualquier arreglo de ese doble cast va aparte de las listas de IRIs.
        if (propertyMap?.caster) {
            valor = propertyMap.caster.fromModel(valor);
        }
        return valor;
    }

    /**
     * Given a filter obtains the name of the parameter to put
     * in the route for this component to filter by that modelKey.
     * @param filter - The model key we want to stablish a filter for
     * @param property - The property we want to filter
     * @param id - an id (optional) to be able to recognise the parameter later
     */
    public static sParamToFilterBy(filter: Filter, id: string, property?: string): string | null {
        const keyFilter = Object.entries(this.FILTERS_NAMES).find(([k, v]) => v === filter);
        return keyFilter ? this.PARAMS_PREFIX + keyFilter[0] + '-' + id + '-' + (property || '') : `unknown-filter-${filter.name}`;
    }

    private _extractId(paramKey: string): string {
        const start = RouteFilterTranslator.PARAMS_PREFIX.length;
        const infixStart = paramKey.indexOf('-', start) + 1;
        const infixEnd = paramKey.indexOf('-', infixStart);
        return paramKey.substring(infixStart, infixEnd);
    }

    /**
     * Converts a filter to a param for the URL
     * @param filter
     * @param id
     */
    public static sFilterToParam(filter: FilterAndData, id: string): { [s: string]: any } {
        const paramKey = RouteFilterTranslator.sParamToFilterBy(filter.filter, id, filter.property ? filter.property.modelKey : undefined);
        const paramValue = RouteFilterTranslator.sParamValueFrom(filter.property, filter.data,
            RouteFilterTranslator.shouldCast(filter.filter));
        return {[paramKey]: paramValue};
    }

    /**
     * Converts a list of filters to params for the URL
     * @param filters
     */
    public static sFiltersToParams(filters: { [id: string]: FilterAndData }): Params {
        return Object.entries(filters).map(([id, f]) => this.sFilterToParam(f, id))
            .reduce((p, c) => Object.assign(p, c), {});
    }

    /**
     * Gets the right filter and property name from the given param key
     * @param param
     * @private
     */
    private static _sFilterAndPropertyName(param: string): [Filter, string | null] | null {
        if (param.startsWith(RouteFilterTranslator.PARAMS_PREFIX)) {
            const start = RouteFilterTranslator.PARAMS_PREFIX.length;
            const idStart = param.indexOf('-', start) + 1;
            const propertyStart = param.indexOf('-', idStart) + 1;
            const filterName = param.substring(start, idStart - 1);
            const filter = RouteFilterTranslator.FILTERS_NAMES[filterName];
            if (filter) {
                const propertyName = param.substring(propertyStart);
                return [filter, propertyName || null];
            }
        }
        return null;
    }

    /**
     * Given a param value to filter by it, returns the real model value which should be used in the filtering.
     * Si el valor no se puede convertir (p. ej. un IRI que no encaja con el patrón de la relación) no lanza:
     * avisa por consola y devuelve null, para que una URL mal formada no tumbe la tabla.
     */
    public paramValueToFilterValue(property: InternalPropertyMap | null, paramValue: string, propertyCast: boolean): any {
        try {
            return this._paramValueToFilterValueOrThrow(property, paramValue, propertyCast);
        } catch (e) {
            console.warn('Filtro de la URL descartado: no se pudo convertir su valor', paramValue, e);
            return null;
        }
    }

    private _paramValueToFilterValueOrThrow(property: InternalPropertyMap | null, paramValue: any, propertyCast: boolean): any {
        try {
            paramValue = JSON.parse(paramValue);
        } catch {
            paramValue = null;
        }
        if (property && propertyCast) {
            const esAnidada = property.type.toString() === TypeStr.NestedModel;
            const aUno = (v: any) => {
                if (esAnidada) {
                    const intrf = this._interfaceProvider.interfaceForModel(property.type.asNestedModel().modelType);
                    return Uri.fromUriStr(v, intrf.uriPattern, intrf.serialiser.model);
                }
                return property.caster ? property.caster.toModel(v) : v;
            };
            // Una lista de IRIs en una relación se convierte elemento a elemento, como un IRI suelto
            const esRelacion = esAnidada || property.type.toString() === TypeStr.Uri;
            if (esRelacion && isArray(paramValue)) {
                return paramValue.map(aUno);
            }
            return aUno(paramValue);
        } else {
            if (isArray(paramValue)) {
                return paramValue.map(v => property.caster ? property.caster.toModel(v) : v)
            }
            return paramValue;
        }
    }

    /**
     * Gets a filter from the given param, it needs a pool of properties to search for the
     * right one in case it is needed to convert the value.
     * @param paramKey
     * @param paramValue
     * @param properties
     */
    public paramToFilter(paramKey: string, paramValue: any, properties: InternalPropertyMap[]): FilterAndData | null {
        const filterAndProp = RouteFilterTranslator._sFilterAndPropertyName(paramKey);
        if (filterAndProp) {
            const [filter, property] = filterAndProp;
            const map = property ? properties.find(p => p.modelKey === property) : null;
            const propertyCast = RouteFilterTranslator.shouldCast(filter);
            try {
                const value = this._paramValueToFilterValueOrThrow(map, paramValue, propertyCast);
                return new FilterAndData(filter, value, map);
            } catch (e) {
                // Valor imposible de convertir: se descarta este filtro y los demás siguen funcionando
                console.warn('Filtro de la URL descartado:', paramKey, paramValue, e);
                return null;
            }
        } else {
            return null;
        }
    }

    /**
     * Checks if the given filter should be casted into and back from the URL
     * @param filter
     */
    public static shouldCast(filter: Filter) {
        switch (filter) {
            case ExistsFilter:
            case GeneralSearchFilter:
            case GreaterThanRangeFilter:
            case GreaterThanOrEqRangeFilter:
            case LowerThanRangeFilter:
            case LowerThanOrEqRangeFilter:
            case BetweenRangeFilter:
            case SimpleSearchFilter:
            case StartSearchFilter:
            case WordStartSearchFilter:
            case PartialSearchFilter:
            case EndSearchFilter:
            case BetweenDateFilter:
                return false;
            case AfterDateFilter:
            case BeforeDateFilter:
            case StrictlyAfterDateFilter:
            case StrictlyBeforeDateFilter:
            case IriSearchFilter:
            case NumericFilter:
            case BooleanFilter:
            case MultipleSearchFilter:
            case ExactSearchFilter:
                return true;
        }
        return true;
    }

    /**
     * Converts a group of params back to filters, it needs a pool of properties to search for the
     * properties (and they are also needed to cast some values).
     * @param params
     * @param properties
     */
    public paramsToFilters(params: Params, properties: InternalPropertyMap[]): { [id: string]: FilterAndData } {
        const obj = {};
        Object.entries(params)
            .forEach(([k, d]) => {
                const filter = this.paramToFilter(k, d, properties);
                if (filter) {
                    obj[this._extractId(k)] = filter;
                }
            });
        return obj;
    }
}
